import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Public-safe set of prediction IDs that have been reviewed by at least one
 * founding member (or admin). Powers the "Reviewed by Founding Member" badge
 * shown on every prediction card — for all visitors, without leaking who
 * reviewed. Reviewer identities and aggregated intelligence are admin-only.
 *
 * The set is read via a SECURITY DEFINER RPC (so it works for everyone without
 * exposing the underlying rows). Because RLS prevents non-owners from reading
 * other members' review rows, we keep the badge fresh for every viewer with a
 * short refetch interval + refetch-on-focus rather than realtime — so a new
 * founding-member review surfaces the badge without a full page reload.
 */
export function useFoundingReviewedPredictions() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["founding-reviewed-prediction-ids", user?.id ?? null],
    // EXECUTE on the RPC is granted to `authenticated` only, so signed-out
    // visitors would throw a permission error on every refetch.
    enabled: !!user,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase.rpc("get_founding_reviewed_prediction_ids");
      if (error) throw error;
      return new Set(((data ?? []) as string[]).filter(Boolean));
    },
  });

  return {
    reviewedIds: query.data ?? new Set<string>(),
    isLoading: query.isLoading,
  };
}
